import { combine, createEvent, createStore, sample } from 'effector';
import { debounce } from 'patronum/debounce';
import {
  cardSearch,
  CardSearchAreas,
  PCardSearch,
  RCardSearch,
} from '../../../../apiRPC/card';
import { Board, getListBoard } from '../../../../apiRPC/board';
import {
  $workspaceList,
  workspaceGetListFX,
} from '../../../../GRID/model/workspace';
import { errorHandlerFx } from '../../../../model/errorHandler';
import { createEffectJWT } from '../../../../model/JWT';

export const searchFiltersOpened = createEvent();
const $catalogRequested = createStore(false).on(
  searchFiltersOpened,
  () => true,
);
const loadSearchBoardsFX = createEffectJWT(async (workspaces: string[]) => {
  const rows = await Promise.all(
    workspaces.map(async (workspace) => {
      const boards: Board[] = [];
      let total = 0;
      do {
        const page = await getListBoard({
          filter: { workspace, limit: 100, offset: boards.length },
        });
        boards.push(...page.rows);
        total = page.total;
        if (!page.rows.length) break;
      } while (boards.length < total);
      return boards;
    }),
  );
  return rows.flat();
});
const $boardsRequest = createStore<string[] | null>(null).on(
  loadSearchBoardsFX,
  (_, params) => params,
);
const searchBoardsLoaded = createEvent<Board[]>();
const searchBoardsFailed = createEvent();
export const $searchBoards = createStore<Board[] | null>(null).on(
  searchBoardsLoaded,
  (_, rows) => rows,
);
export const $searchBoardsLoading = createStore(false)
  .on(loadSearchBoardsFX, () => true)
  .reset(searchBoardsLoaded, searchBoardsFailed);
export const $searchCatalogError = createStore(false)
  .on(searchBoardsFailed, () => true)
  .reset(searchFiltersOpened, loadSearchBoardsFX, searchBoardsLoaded);

sample({
  clock: searchFiltersOpened,
  source: $workspaceList,
  filter: (list) => list === null,
  fn: () => undefined,
  target: workspaceGetListFX,
});
sample({
  clock: [searchFiltersOpened, $workspaceList.updates],
  source: { list: $workspaceList, requested: $catalogRequested },
  filter: ({ list, requested }) => list !== null && requested,
  fn: ({ list }) => list?.rows.map(({ uuid }) => uuid) || [],
  target: loadSearchBoardsFX,
});
sample({
  clock: loadSearchBoardsFX.done,
  source: $boardsRequest,
  filter: (active, { params }) => active === params,
  fn: (_, { result }) => result,
  target: searchBoardsLoaded,
});
sample({
  clock: loadSearchBoardsFX.fail,
  source: $boardsRequest,
  filter: (active, { params }) => active === params,
  target: searchBoardsFailed,
});
sample({
  clock: workspaceGetListFX.fail,
  source: { requested: $catalogRequested, list: $workspaceList },
  filter: ({ requested, list }) => requested && list === null,
  target: searchBoardsFailed,
});

export type SearchSettings = {
  searchIn: CardSearchAreas;
  startDate: string;
  endDate: string;
  workspaces: string[];
  boards: string[];
};

export function getSearchSettingsError(settings: SearchSettings) {
  if (!Object.values(settings.searchIn).some(Boolean)) {
    return 'Выберите хотя бы одну область поиска';
  }
  if (
    settings.startDate &&
    settings.endDate &&
    settings.startDate > settings.endDate
  ) {
    return 'Дата начала не должна быть позже даты окончания';
  }
  return '';
}

export const searchSettingsApplied = createEvent<SearchSettings>();
export const DEFAULT_SEARCH_SETTINGS: SearchSettings = {
  searchIn: { title: true, description: true, comments: true, uuid: true },
  startDate: '',
  endDate: '',
  workspaces: [],
  boards: [],
};
export const $searchSettings = createStore(DEFAULT_SEARCH_SETTINGS).on(
  searchSettingsApplied,
  (state, settings) => (getSearchSettingsError(settings) ? state : settings),
);

const limit = 30;
const cardSearchFX = errorHandlerFx(createEffectJWT(cardSearch));
export const searchCards = createEvent<string>();
export const onResetCardsList = createEvent();
export const onLoadCards = createEvent();
const searchRequested = createEvent<PCardSearch | null>();
const validSearchRequested = searchRequested.filter({
  fn: (params): params is PCardSearch => params !== null,
});
const searchLoaded = createEvent<{
  params: PCardSearch;
  result: RCardSearch;
}>();
const requestFinished = createEvent();
export const $searchLoading = createStore(false)
  .on(searchRequested, (_, params) => params !== null)
  .on(cardSearchFX, () => true)
  .reset(requestFinished, onResetCardsList);
const $query = createStore('')
  .on(searchCards, (_, text) => text.trim())
  .reset(onResetCardsList);
const $filter = createStore<PCardSearch | null>(null)
  .on(searchRequested, (_, params) => params)
  .reset(onResetCardsList);
const $activeRequest = createStore<PCardSearch | null>(null)
  .on(cardSearchFX, (_, params) => params)
  .reset(searchRequested, onResetCardsList, requestFinished);
export const $cardsList = createStore<RCardSearch['rows'] | null>(null)
  .on(searchLoaded, (state, { params, result }) =>
    params.offset ? [...(state || []), ...result.rows] : result.rows,
  )
  .reset(searchRequested, onResetCardsList);
const $totalRows = createStore(0)
  .on(searchLoaded, (_, { result }) => result.total)
  .reset(searchRequested, onResetCardsList);

sample({
  clock: [searchCards, searchSettingsApplied],
  source: { text: $query, settings: $searchSettings },
  fn: ({ text, settings }): PCardSearch | null =>
    text.length >= 3 && text.length <= 80 && !getSearchSettingsError(settings)
      ? {
          text,
          limit,
          offset: 0,
          searchIn: settings.searchIn,
          ...(settings.workspaces.length
            ? { workspaces: settings.workspaces }
            : {}),
          ...(settings.boards.length ? { boards: settings.boards } : {}),
          ...(settings.startDate ? { startDate: settings.startDate } : {}),
          ...(settings.endDate ? { endDate: settings.endDate } : {}),
        }
      : null,
  target: searchRequested,
});

sample({
  clock: debounce({ source: validSearchRequested, timeout: 300 }),
  source: $filter,
  filter: (current, params) => current === params,
  fn: (_, params) => params,
  target: cardSearchFX,
});

sample({
  clock: cardSearchFX.done,
  source: $activeRequest,
  filter: (active, { params }) => active === params,
  fn: (_, payload) => payload,
  target: searchLoaded,
});
sample({ clock: searchLoaded, target: requestFinished });
sample({
  clock: cardSearchFX.fail,
  source: $activeRequest,
  filter: (active, { params }) => active === params,
  target: requestFinished,
});

const $nextPage = combine(
  {
    filter: $filter,
    rows: $cardsList,
    total: $totalRows,
    active: $activeRequest,
  },
  ({ filter, rows, total, active }): PCardSearch | null => {
    if (!filter || !rows?.length || rows.length >= total || active) return null;
    return { ...filter, offset: rows.length };
  },
);
sample({
  clock: onLoadCards,
  source: $nextPage,
  filter: (params): params is PCardSearch => params !== null,
  target: cardSearchFX,
});
